import { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  WIZARD_STEPS,
  getStepIndexFromPath,
  getStepTitle,
  isValidStepIndex,
  getRouteForNavigation,
  StepKey,
} from '../config';
import { isStepComplete } from '../utils';
import { useAppSelector } from 'modules/state-mgmt/store';

interface UseWizardNavigationProps {
  stepTitle?: string;
  content: React.ReactNode;
  fields?: Array<{ name: string; id: string; isRequired?: boolean; isSkippable?: boolean; skippableTill?: number }>;
}

export const useWizardNavigation = ({ stepTitle, content, fields }: UseWizardNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    onboarding,
    profileFieldConstraintsMap,
    canCreateUnverifiedWorkers,
    isArchivedProject,
    visitedWizardSteps,
    hasReachedConsentForm,
    project,
  } = useAppSelector(state => ({
    onboarding: state.onboarding,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    canCreateUnverifiedWorkers: state.cache.canCreateUnverifiedWorkers,
    isArchivedProject: state.cache.project.status === 3,
    visitedWizardSteps: state.onboarding.temp?.visitedWizardSteps || [],
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm || false,
    project: state.cache.project,
  }));

  const isStepDisabled = useCallback(
    step => {
      if (step.key === StepKey.REGISTRATION) {
        // The consent sub-steps live under Worker Registration, so this menu stays
        // active (not greyed) until the whole flow is done. isStepComplete already
        // requires the signature, so completion == the group is finished.
        return isStepComplete({
          stepKey: step.key as StepKey,
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
        });
      }
      if (step.key === StepKey.TAKE_PHOTO) {
        return isArchivedProject;
      }
      return false;
    },
    [
      onboarding,
      profileFieldConstraintsMap,
      canCreateUnverifiedWorkers,
      isArchivedProject,
    ],
  );

  // Generate steps with proper titles and configuration
  const steps = useMemo(() => {
    const filteredSteps = WIZARD_STEPS.map((stepConfig, absoluteIndex) => ({
      key: stepConfig.key,
      title: getStepTitle(stepConfig, stepTitle, t),
      icon: stepConfig.icon(),
      fields: fields,
      content: content,
      disabled: isStepDisabled(stepConfig),
      isCompleted: isStepComplete({
        stepKey: stepConfig.key as StepKey,
        onboarding,
        profileFieldConstraintsMap,
        canCreateUnverifiedWorkers,
      }),
      absoluteStepIndex: absoluteIndex, // Store the absolute index in WIZARD_STEPS
    })).filter(step => {
      // Filter out steps that should be skipped (i.e., all fields not visible)
      const stepConfig = WIZARD_STEPS.find(s => s.key === step.key);
      if (stepConfig?.shouldSkip) {
        const state = {
          onboarding,
          cache: {
            profileFieldConstraintsMap,
            canCreateUnverifiedWorkers,
            project,
          },
        };
        return !stepConfig.shouldSkip(state as any);
      }
      return true;
    });

    // Calculate isClickable for each step.
    //
    // Rule 1: First step is always clickable.
    //
    // Rules 2 & 3 only apply when hasReachedConsentForm is false.
    // Once the user is sent back from the consent form to fill mandatory skippable fields,
    // these bypasses are disabled so they cannot jump ahead while fields remain incomplete.
    //
    // Rule 2: Step was explicitly visited (user pressed Next leaving this step).
    //
    // Rule 3 (next-after-visited): The immediately preceding step was visited, meaning the
    // user arrived at this step via Next. This is needed because visitedWizardSteps records
    // the step you are LEAVING, not the step you are arriving at. Without this rule, the
    // destination step (e.g. Certifications) would be unreachable after the user navigates
    // back via the sidebar, even though they already reached it.
    //
    // Rule 4: Strict completion fallback — all previous steps are fully complete.
    return filteredSteps.map((step, index) => ({
      ...step,
      isClickable:
        index === 0 || // Rule 1
        (!hasReachedConsentForm && visitedWizardSteps.includes(step.key as StepKey)) || // Rule 2
        (!hasReachedConsentForm && index > 0 && visitedWizardSteps.includes(filteredSteps[index - 1].key as StepKey)) || // Rule 3
        filteredSteps.slice(0, index).every(prevStep => prevStep.isCompleted), // Rule 4
    }));
  }, [
    stepTitle,
    content,
    fields,
    onboarding,
    profileFieldConstraintsMap,
    canCreateUnverifiedWorkers,
    t,
    visitedWizardSteps,
    hasReachedConsentForm,
    isStepDisabled,
  ]);

  // Determine current step index based on URL path
  const currentStepIndex = useMemo(() => {
    return getStepIndexFromPath(location.pathname);
  }, [location.pathname]);

  // Handle step navigation with route mapping (for sidebar step clicks)
  const onStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex !== currentStepIndex && isValidStepIndex(stepIndex)) {
        const targetStep = WIZARD_STEPS[stepIndex];
        // If target step has sub-steps, navigate to the first sub-step
        const route = targetStep.subSteps ? getRouteForNavigation(stepIndex, 0) : getRouteForNavigation(stepIndex);
        navigate(route);
      }
    },
    [navigate, currentStepIndex],
  );

  return {
    steps,
    currentStepIndex,
    onStepChange,
  };
};
